import { paginationFailureReason } from '@models/planning/electronic-process-pagination.model'

/**
 * Livro eletrônico do processo (spec 001 do document-generator-electronic-process-workload-backend): o PDF único com
 * todos os documentos e anexos, na ordem do processo e com o carimbo de folha, gerado em segundo plano pelo workload.
 * Os arquivos valem por 72 horas; o histórico é permanente.
 */
export enum ElectronicProcessBookStatus {
	PENDING = 1,
	PROCESSING = 2,
	COMPLETED = 3,
	/** Nada mudou: o livro de uma geração anterior foi reaproveitado, com prazo novo. */
	UNCHANGED = 4,
	FAILED = 5
}

export enum ElectronicProcessBookPhase {
	VERIFYING = 1,
	STAMPING = 2,
	ASSEMBLING = 3,
	FINALIZING = 4
}

/** Um arquivo do livro (o principal de um documento ou um anexo) e as folhas dele. */
export interface ElectronicProcessBookUnit {
	seq?: number
	/** `DOCUMENT` ou `ATTACHMENT`. */
	kind?: string
	documentId?: string
	attachmentId?: string
	label?: string
	firstFolha?: number | null
	lastFolha?: number | null
}

export interface ElectronicProcessBookErrorUnit {
	seq?: number
	documentId?: string
	attachmentId?: string
	label?: string
	code?: string
	reason?: string
	detail?: string
}

export interface ElectronicProcessBookError {
	code?: string
	message?: string
	units?: ElectronicProcessBookErrorUnit[]
}

/** Link assinado para o PDF: de visualização (até 60 min) ou de download (até 15 min, com nome de arquivo). */
export interface ElectronicProcessBookUrl {
	url: string
	expiresAt?: string | null
	filename?: string | null
}

export type BookTone = 'success' | 'danger' | 'warning' | 'info' | 'secondary'

const STATUS: Record<ElectronicProcessBookStatus, { label: string; tone: BookTone; icon: string }> = {
	[ElectronicProcessBookStatus.PENDING]: { label: 'Na fila', tone: 'secondary', icon: 'clock' },
	[ElectronicProcessBookStatus.PROCESSING]: { label: 'Gerando', tone: 'info', icon: 'spinner' },
	[ElectronicProcessBookStatus.COMPLETED]: { label: 'Concluído', tone: 'success', icon: 'circle-check' },
	[ElectronicProcessBookStatus.UNCHANGED]: { label: 'Sem alterações', tone: 'success', icon: 'circle-check' },
	[ElectronicProcessBookStatus.FAILED]: { label: 'Falhou', tone: 'danger', icon: 'circle-exclamation' }
}

const PHASES: Record<ElectronicProcessBookPhase, string> = {
	[ElectronicProcessBookPhase.VERIFYING]: 'Verificando alterações',
	[ElectronicProcessBookPhase.STAMPING]: 'Carimbando documentos',
	[ElectronicProcessBookPhase.ASSEMBLING]: 'Montando o PDF',
	[ElectronicProcessBookPhase.FINALIZING]: 'Finalizando'
}

/** O motivo de uma falha da geração, como o usuário entende (spec 001, RF 25 e 27). */
export const bookFailureReason = (unit?: ElectronicProcessBookErrorUnit | null): string => {
	switch (unit?.code) {
		case 'PAGE_COUNT_MISMATCH':
			return unit.detail
				? `Número de páginas diferente do registrado na paginação (${unit.detail})`
				: 'Número de páginas diferente do registrado na paginação'
		case 'PAGINATION_NOT_CONTIGUOUS':
			return 'Paginação gravada com folhas fora de sequência'
		default:
			return paginationFailureReason(unit)
	}
}

/** "1,2 MB": o tamanho do PDF no histórico. */
export const formatBookSize = (bytes?: number | null): string | null => {
	if (bytes == null || bytes < 0) return null
	if (bytes < 1024) return `${bytes} B`

	const units = ['KB', 'MB', 'GB']
	let value = bytes / 1024
	let index = 0

	while (value >= 1024 && index < units.length - 1) {
		value /= 1024
		index++
	}

	return `${value.toLocaleString('pt-BR', { maximumFractionDigits: value < 10 ? 1 : 0 })} ${units[index]}`
}

/** Uma geração do livro (`GET electronic-processes/{id}/books/{bookId}` e o tópico STOMP do processo). */
export class ElectronicProcessBook {
	id?: string
	processId?: string
	status?: ElectronicProcessBookStatus
	phase?: ElectronicProcessBookPhase | null
	percent = 0
	processedUnits = 0
	totalUnits = 0
	processedPages?: number | null
	totalPages?: number | null
	attempt = 0
	startedAt?: string | null
	finishedAt?: string | null
	expiresAt?: string | null
	reusedFromId?: string | null
	pdfSizeBytes?: number | null
	units?: ElectronicProcessBookUnit[] | null
	error?: ElectronicProcessBookError | null
	createdBy?: string | null
	createdAt?: string | null
	/** Calculado pelo servidor: pronto, mas com os arquivos vencidos. */
	expired = false

	constructor(source?: Partial<ElectronicProcessBook>) {
		if (source) {
			Object.assign(this, source)
		}
	}

	/** Na fila ou gerando: no máximo uma por processo. */
	get active(): boolean {
		return this.status === ElectronicProcessBookStatus.PENDING || this.status === ElectronicProcessBookStatus.PROCESSING
	}

	get failed(): boolean {
		return this.status === ElectronicProcessBookStatus.FAILED
	}

	/** Concluído ou sem alterações: tem PDF. */
	get ready(): boolean {
		return this.status === ElectronicProcessBookStatus.COMPLETED || this.status === ElectronicProcessBookStatus.UNCHANGED
	}

	/** Pronto e dentro do prazo dos arquivos: abre e baixa. */
	get available(): boolean {
		return this.ready && !this.isExpired()
	}

	get statusLabel(): string {
		if (this.ready && this.isExpired()) return 'Expirado'
		return STATUS[this.status]?.label ?? 'Não informado'
	}

	get tone(): BookTone {
		if (this.ready && this.isExpired()) return 'secondary'
		return STATUS[this.status]?.tone ?? 'secondary'
	}

	get icon(): string {
		if (this.ready && this.isExpired()) return 'clock'
		return STATUS[this.status]?.icon ?? 'circle-info'
	}

	/** A etapa atual, como a spec lista (RF 6): "Na fila", "Carimbando documentos (3 de 12)"… */
	get progressLabel(): string {
		if (this.status === ElectronicProcessBookStatus.PENDING) return 'Na fila'

		const phase = PHASES[this.phase] ?? 'Gerando'

		if (this.phase === ElectronicProcessBookPhase.STAMPING && this.totalUnits) {
			return `${phase} (${this.processedUnits} de ${this.totalUnits})`
		}

		return phase
	}

	get sizeLabel(): string | null {
		return formatBookSize(this.pdfSizeBytes)
	}

	/** Sumário do livro (RF 17): cada documento e anexo com as suas folhas, na ordem do livro. */
	get summary(): ElectronicProcessBookUnit[] {
		return [...(this.units ?? [])].filter(unit => unit.firstFolha != null).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
	}

	get failures(): ElectronicProcessBookErrorUnit[] {
		return this.error?.units ?? []
	}

	isExpired(now = Date.now()): boolean {
		if (this.expired) return true
		return !!this.expiresAt && new Date(this.expiresAt).getTime() <= now
	}
}
